/**
 * FootingDesignPage — reinforced concrete isolated spread footing.
 *
 * Left rail: section + materials + soil + loads + reinforcement.
 * Center: plan view + elevation + bearing-pressure stress diagram + 3D toggle.
 * Right rail: geometry, bearing, shear, punching, flexion, verifications.
 */

import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFootingStore } from '@/features/zapatas/store';
import { useFootingAnalysis } from '@/features/zapatas/useFootingAnalysis';
import { useExportFooting } from '@/features/zapatas/useExport';
import { Footing3DView } from '@/features/zapatas/Footing3DView';
import { RhoBar } from '@/components/ui/RhoBar';
import { DesignToolbar } from '@/features/designs/DesignToolbar';
import { SaveDesignDialog } from '@/features/designs/SaveDesignDialog';
import { useDesign } from '@/features/designs/useDesigns';
import type { FootingAnalyzeRequest, RebarDiam } from '@/features/zapatas/types';

const REBAR_DIAMS: RebarDiam[] = ['3/8', '1/2', '5/8', '3/4', '1', '1 3/8'];

/**
 * When `hideHeader` is true, the page renders without its top header bar
 * (used when embedded under another header such as the unified
 * FootingsPage with the type picker). Export buttons are rendered inline
 * above the main content in that case.
 */
export function FootingDesignPage({ hideHeader = false }: { hideHeader?: boolean } = {}) {
  const request = useFootingStore((s) => s.request);
  const setSection = useFootingStore((s) => s.setSection);
  const setMaterials = useFootingStore((s) => s.setMaterials);
  const setSoil = useFootingStore((s) => s.setSoil);
  const setLoads = useFootingStore((s) => s.setLoads);
  const setReinforcement = useFootingStore((s) => s.setReinforcement);
  const loadRequest = useFootingStore((s) => s.loadRequest);

  // URL hydration: /app/footings/:id loads that saved design into the store
  // when the subtype matches "isolated". Other subtypes are handled by their
  // own sibling pages (Strip/Combined/Connected) mounted under FootingsPage.
  const params = useParams<{ id?: string }>();
  const designId =
    params.id && params.id !== 'new' && /^\d+$/.test(params.id) ? Number(params.id) : null;
  const designQuery = useDesign(designId);
  useEffect(() => {
    if (designQuery.data && designQuery.data.subtype === 'isolated') {
      loadRequest(designQuery.data.payload as unknown as FootingAnalyzeRequest);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designQuery.data?.id]);

  const [saveOpen, setSaveOpen] = useState(false);

  const { data, isFetching, isError, error } = useFootingAnalysis(request);
  const exporter = useExportFooting();
  const [view, setView] = useState<'2D' | '3D'>('2D');

  // Hidden 3D viewer for PDF snapshot capture (always mounted off-screen).
  const snapshotContainerRef = useRef<HTMLDivElement | null>(null);
  const captureSnapshot = (): string | null => {
    const el = snapshotContainerRef.current?.querySelector('canvas') as HTMLCanvasElement | null;
    if (!el) return null;
    try {
      return el.toDataURL('image/png');
    } catch (e) {
      console.warn('[zapata-3d] toDataURL failed:', e);
      return null;
    }
  };

  // Match the saved design only when its subtype agrees with this page —
  // otherwise the "Actualizar" button would overwrite an Aislada design
  // with Corrida payload (and vice versa).
  const matchingDesign =
    designQuery.data && designQuery.data.subtype === 'isolated' ? designQuery.data : null;

  return (
    <div className="flex h-full flex-col">
      {!hideHeader && (
        <header
          className="flex h-14 shrink-0 items-center gap-6 border-b px-6"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-subtle)' }}
        >
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
              Software E.060 + E.050
            </div>
            <div className="text-sm font-semibold leading-none" style={{ color: 'var(--text-primary)' }}>
              Diseño de zapata aislada
            </div>
          </div>
        </header>
      )}

      {/* Standardised toolbar across all design pages: status dot + PDF/XLSX
          export + Save/Actualizar. Visible in both standalone and embedded
          (FootingsPage type-picker) modes — the export buttons used to live
          inline in the header and were easy to miss. */}
      <DesignToolbar
        status={isError ? 'error' : isFetching ? 'busy' : 'ready'}
        statusLabel={isError ? `Error: ${String(error)}` : undefined}
        onExportPdf={() => exporter.trigger('pdf', request, captureSnapshot())}
        onExportXlsx={() => exporter.trigger('xlsx', request, captureSnapshot())}
        busyExport={exporter.busy}
        onSave={() => setSaveOpen(true)}
        isUpdate={!!matchingDesign}
      />
      <SaveDesignDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        module="zapatas"
        subtype="isolated"
        payload={request as unknown as Record<string, unknown>}
        existing={matchingDesign}
      />

      <div className="flex flex-1 overflow-hidden">
        {/* LEFT — Inputs */}
        <aside
          className="flex w-[300px] shrink-0 flex-col gap-3 overflow-y-auto border-r p-3 text-[11px]"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
        >
          <Group title="Geometría de la zapata">
            <NumberRow label="Lx (cm)" value={request.section.Lx} onChange={(v) => setSection({ Lx: v })} step={5} />
            <NumberRow label="Ly (cm)" value={request.section.Ly} onChange={(v) => setSection({ Ly: v })} step={5} />
            <NumberRow label="Hf (cm) — peralte" value={request.section.Hf} onChange={(v) => setSection({ Hf: v })} step={5} />
            <NumberRow label="df (cm) — prof. cimentación" value={request.section.df} onChange={(v) => setSection({ df: v })} step={10} />
          </Group>

          <Group title="Columna sobre la zapata">
            <NumberRow label="bc (cm) — ancho en X" value={request.section.bc} onChange={(v) => setSection({ bc: v })} step={5} />
            <NumberRow label="hc (cm) — ancho en Y" value={request.section.hc} onChange={(v) => setSection({ hc: v })} step={5} />
            <NumberRow
              label="x_col (cm) — centro col en X"
              value={request.section.x_col ?? request.section.Lx / 2}
              onChange={(v) => setSection({ x_col: v })}
              step={5}
            />
            <NumberRow
              label="y_col (cm) — centro col en Y"
              value={request.section.y_col ?? request.section.Ly / 2}
              onChange={(v) => setSection({ y_col: v })}
              step={5}
            />
            <button
              type="button"
              onClick={() => setSection({ x_col: request.section.Lx / 2, y_col: request.section.Ly / 2 })}
              className="mt-1 w-full rounded-[var(--radius-sm)] border px-2 py-1 text-[10px] transition-colors hover:bg-[var(--surface-2)]"
              style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)' }}
            >
              ⤢ Centrar columna
            </button>
          </Group>

          <Group title="Materiales">
            <NumberRow label="f'c (kgf/cm²)" value={request.materials.fc} onChange={(v) => setMaterials({ fc: v })} step={10} />
            <NumberRow label="fy (kgf/cm²)" value={request.materials.fy} onChange={(v) => setMaterials({ fy: v })} step={100} />
            <NumberRow label="Recubrim. (cm)" value={request.materials.cover} onChange={(v) => setMaterials({ cover: v })} step={0.5} />
          </Group>

          <Group title="Suelo (E.050)">
            <NumberRow label="σ_adm (kgf/cm²)" value={request.soil.sigma_adm} onChange={(v) => setSoil({ sigma_adm: v })} step={0.1} />
            <NumberRow label="γ_s suelo (kgf/m³)" value={request.soil.gamma_s} onChange={(v) => setSoil({ gamma_s: v })} step={50} />
            <NumberRow label="γ_c concreto (kgf/m³)" value={request.soil.gamma_c} onChange={(v) => setSoil({ gamma_c: v })} step={50} />
            <NumberRow label="Factor σ_adm" value={request.soil.sigma_factor} onChange={(v) => setSoil({ sigma_factor: v })} step={0.05} />
          </Group>

          <Group title="Cargas servicio">
            <NumberRow label="P (kgf)" value={request.loads.P} onChange={(v) => setLoads({ P: v })} step={1000} />
            <NumberRow label="Mx (kgf·cm)" value={request.loads.Mx} onChange={(v) => setLoads({ Mx: v })} step={50000} />
            <NumberRow label="My (kgf·cm)" value={request.loads.My} onChange={(v) => setLoads({ My: v })} step={50000} />
          </Group>

          <Group title="Cargas factoradas">
            <NumberRow label="Pu (kgf)" value={request.loads.Pu} onChange={(v) => setLoads({ Pu: v })} step={1000} />
            <NumberRow label="Mux (kgf·cm)" value={request.loads.Mux} onChange={(v) => setLoads({ Mux: v })} step={50000} />
            <NumberRow label="Muy (kgf·cm)" value={request.loads.Muy} onChange={(v) => setLoads({ Muy: v })} step={50000} />
          </Group>

          <Group title="Refuerzo inferior">
            <SelectRow
              label="Diámetro en X"
              value={request.reinforcement.diam_x}
              options={REBAR_DIAMS.map((d) => ({ value: d, label: `Ø${d}"` }))}
              onChange={(v) => setReinforcement({ diam_x: v as RebarDiam })}
            />
            <NumberRow label="Cantidad barras X" value={request.reinforcement.n_x} onChange={(v) => setReinforcement({ n_x: Math.max(2, Math.round(v)) })} step={1} />
            <SelectRow
              label="Diámetro en Y"
              value={request.reinforcement.diam_y}
              options={REBAR_DIAMS.map((d) => ({ value: d, label: `Ø${d}"` }))}
              onChange={(v) => setReinforcement({ diam_y: v as RebarDiam })}
            />
            <NumberRow label="Cantidad barras Y" value={request.reinforcement.n_y} onChange={(v) => setReinforcement({ n_y: Math.max(2, Math.round(v)) })} step={1} />
          </Group>

          <Group title="Anclaje barras de columna (mechones)">
            <SelectRow
              label="Diámetro barra columna"
              value={request.reinforcement.diam_col_bar}
              options={REBAR_DIAMS.map((d) => ({ value: d, label: `Ø${d}"` }))}
              onChange={(v) => setReinforcement({ diam_col_bar: v as RebarDiam })}
            />
            <NumberRow
              label="N° de mechones"
              value={request.reinforcement.n_col_bars}
              onChange={(v) => setReinforcement({ n_col_bars: Math.max(4, Math.round(v)) })}
              step={1}
            />
            <label className="flex items-center justify-between gap-2 py-0.5">
              <span className="text-[10.5px]" style={{ color: 'var(--text-secondary)' }}>
                Gancho 90° en el fondo
              </span>
              <input
                type="checkbox"
                checked={request.reinforcement.col_bar_hooked}
                onChange={(e) => setReinforcement({ col_bar_hooked: e.target.checked })}
                className="h-4 w-4"
              />
            </label>
            <div className="text-[9.5px] italic mt-1" style={{ color: 'var(--text-muted)', lineHeight: 1.35 }}>
              Las barras de columna bajan hasta el fondo de la zapata y se anclan con gancho
              estándar de 90° (cola = 12·db). Si Hf no alcanza para ldh recto se requiere gancho.
            </div>
          </Group>
        </aside>

        {/* CENTER */}
        <main className="flex-1 overflow-auto p-4" style={{ background: 'var(--surface-canvas)' }}>
          {data && (
            <div className="flex flex-col gap-4">
              <DiagramCard
                title={view === '3D' ? 'Vista 3D — zapata + columna' : 'Vista en planta'}
                toolbar={
                  <div className="flex overflow-hidden rounded-[var(--radius-sm)] border" style={{ borderColor: 'var(--border-default)' }}>
                    {(['2D', '3D'] as const).map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setView(m)}
                        className="h-7 px-3 text-[10px] font-semibold tracking-[0.12em]"
                        style={{
                          background: view === m ? 'var(--accent)' : 'transparent',
                          color: view === m ? 'var(--surface-base)' : 'var(--text-secondary)',
                        }}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                }
              >
                {view === '3D' ? (
                  <div style={{ width: 880, height: 480 }}>
                    <Footing3DView
                      Lx={request.section.Lx}
                      Ly={request.section.Ly}
                      Hf={request.section.Hf}
                      bc={request.section.bc}
                      hc={request.section.hc}
                      df={request.section.df}
                      cover={request.materials.cover}
                      x_col={data.geometry.x_col}
                      y_col={data.geometry.y_col}
                      diam_x={request.reinforcement.diam_x}
                      n_x={request.reinforcement.n_x}
                      diam_y={request.reinforcement.diam_y}
                      n_y={request.reinforcement.n_y}
                      diam_col_bar={request.reinforcement.diam_col_bar}
                      n_col_bars={request.reinforcement.n_col_bars}
                      col_bar_hooked={request.reinforcement.col_bar_hooked}
                      ldh_required={data.anchorage.ldh_required}
                    />
                  </div>
                ) : (
                  <FootingPlanDiagram
                    Lx={request.section.Lx}
                    Ly={request.section.Ly}
                    bc={request.section.bc}
                    hc={request.section.hc}
                    x_col={data.geometry.x_col}
                    y_col={data.geometry.y_col}
                    n_x={request.reinforcement.n_x}
                    n_y={request.reinforcement.n_y}
                    diam_x={request.reinforcement.diam_x}
                    diam_y={request.reinforcement.diam_y}
                    d_x={data.geometry.d_x}
                    d_y={data.geometry.d_y}
                    punch_b0_x={request.section.bc + data.punching.d_avg}
                    punch_b0_y={request.section.hc + data.punching.d_avg}
                    onColumnMove={(x, y) => setSection({ x_col: x, y_col: y })}
                  />
                )}
              </DiagramCard>

              <DiagramCard title="Elevación — corte por la columna (vista en X)">
                <FootingElevationDiagram
                  Lx={request.section.Lx}
                  Hf={request.section.Hf}
                  bc={request.section.bc}
                  x_col={data.geometry.x_col}
                  df={request.section.df}
                  sigma_max={data.bearing.sigma_max}
                  sigma_min={data.bearing.sigma_min}
                  sigma_adm_eff={data.bearing.sigma_adm_eff}
                  hooked={data.anchorage.hooked}
                  hook_tail={data.anchorage.hook_tail_length}
                />
              </DiagramCard>

              <DiagramCard title="Envolvente de diseño (P, M) — capacidad por presión sobre el suelo">
                <div className="w-full">
                  <BearingEnvelopeChart
                    Lx={request.section.Lx}
                    Ly={request.section.Ly}
                    Hf={request.section.Hf}
                    df={request.section.df}
                    bc={request.section.bc}
                    hc={request.section.hc}
                    gamma_s={request.soil.gamma_s}
                    gamma_c={request.soil.gamma_c}
                    sigma_adm_eff={data.bearing.sigma_adm_eff}
                    P={request.loads.P}
                    Mx={request.loads.Mx}
                    My={request.loads.My}
                    x_col={data.geometry.x_col}
                    y_col={data.geometry.y_col}
                  />
                  <div className="mt-2 text-[10px] text-center" style={{ color: 'var(--text-muted)', lineHeight: 1.4 }}>
                    Pares <strong>(P, M)</strong> en servicio cuya presión sobre el suelo no excede σ_adm
                    <em> y</em> conservan contacto pleno (σ_min ≥ 0). El rombo se define por:
                    <span className="font-mono mx-1">σ_max ≤ σ_adm</span> (rampa decreciente) y
                    <span className="font-mono mx-1">σ_min ≥ 0</span> (rampa creciente).
                    El punto rojo o verde es la demanda actual del usuario; debe quedar dentro de ambas envolventes.
                  </div>
                </div>
              </DiagramCard>
            </div>
          )}
        </main>

        {/* RIGHT — Results */}
        <aside
          className="flex w-[340px] shrink-0 flex-col gap-3 overflow-y-auto border-l p-3 text-[11px]"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
        >
          {data && (
            <>
              <ResultCard title="Geometría y pesos">
                <KV label="Área zapata" value={`${(data.geometry.A / 10000).toFixed(2)} m² (${data.geometry.A.toFixed(0)} cm²)`} />
                <KV label="Peso propio concreto" value={`${(data.geometry.self_weight / 1000).toFixed(2)} tonf`} />
                <KV label="Peso suelo encima" value={`${(data.geometry.soil_above_weight / 1000).toFixed(2)} tonf`} />
                <KV label="d efectivo X" value={`${data.geometry.d_x.toFixed(1)} cm`} />
                <KV label="d efectivo Y" value={`${data.geometry.d_y.toFixed(1)} cm`} />
              </ResultCard>

              <ResultCard title="Presión sobre el suelo (servicio)">
                <KV label="P total" value={`${(data.bearing.P_total / 1000).toFixed(2)} tonf`} />
                <KV label="e_x" value={`${data.bearing.e_x.toFixed(2)} cm`} />
                <KV label="e_y" value={`${data.bearing.e_y.toFixed(2)} cm`} />
                <KV label="σ_max" value={`${data.bearing.sigma_max.toFixed(3)} kgf/cm² (${(data.bearing.sigma_max * 10).toFixed(2)} t/m²)`} mono />
                <KV label="σ_min" value={`${data.bearing.sigma_min.toFixed(3)} kgf/cm²`} mono />
                <KV label="σ_adm efectivo" value={`${data.bearing.sigma_adm_eff.toFixed(3)} kgf/cm²`} />
                <div className="mt-1 text-[10px]" style={{ color: data.bearing.ok ? 'var(--success)' : 'var(--danger)' }}>
                  {data.bearing.ok ? '✓ ' : '✗ '}{data.bearing.note}
                </div>
              </ResultCard>

              <ResultCard title="Cortante 1D (a d de la cara)">
                <KV label="X — Vu" value={`${(data.one_way_shear.in_X.Vu / 1000).toFixed(2)} tonf`} />
                <KV label="X — φVc" value={`${(data.one_way_shear.in_X.phi_Vc / 1000).toFixed(2)} tonf`} />
                <KV label="Y — Vu" value={`${(data.one_way_shear.in_Y.Vu / 1000).toFixed(2)} tonf`} />
                <KV label="Y — φVc" value={`${(data.one_way_shear.in_Y.phi_Vc / 1000).toFixed(2)} tonf`} />
                <div className="mt-1 text-[10px]" style={{ color: data.one_way_shear.ok ? 'var(--success)' : 'var(--danger)' }}>
                  {data.one_way_shear.ok ? '✓ Cortante en una dirección OK' : '✗ REVISAR cortante 1D (aumentar Hf)'}
                </div>
              </ResultCard>

              <ResultCard title="Punzonado (a d/2 de la columna)">
                <KV label="d promedio" value={`${data.punching.d_avg.toFixed(1)} cm`} />
                <KV label="b0 perímetro" value={`${data.punching.b0.toFixed(1)} cm`} />
                <KV label="βc columna" value={data.punching.beta_c.toFixed(2)} />
                <KV label="Vu punzonado" value={`${(data.punching.Vu_p / 1000).toFixed(2)} tonf`} />
                <KV label="Vc gobernante" value={`${(data.punching.Vc / 1000).toFixed(2)} tonf`} />
                <KV label="φVc" value={`${(data.punching.phi_Vc / 1000).toFixed(2)} tonf`} />
                <div className="mt-1 text-[10px]" style={{ color: data.punching.ok ? 'var(--success)' : 'var(--danger)' }}>
                  {data.punching.ok ? '✓ Punzonado OK' : '✗ REVISAR punzonado (aumentar Hf)'}
                </div>
              </ResultCard>

              <ResultCard title={`Anclaje mechones (ACI 25.4) ${data.anchorage.hooked ? '— gancho 90°' : '— recto'}`}>
                <KV label="Diámetro barra" value={`Ø${data.anchorage.diameter}" (db=${data.anchorage.db.toFixed(2)} cm)`} mono />
                <KV
                  label={data.anchorage.hooked ? 'ldh requerido' : 'ld recto requerido'}
                  value={`${data.anchorage.ldh_required.toFixed(1)} cm`}
                />
                <KV label="d disponible (Hf − rec − db)" value={`${data.anchorage.d_available.toFixed(1)} cm`} />
                {data.anchorage.hooked && (
                  <KV label="Cola del gancho (12·db)" value={`${data.anchorage.hook_tail_length.toFixed(1)} cm`} />
                )}
                <div className="mt-1 text-[10px]" style={{ color: data.anchorage.ok ? 'var(--success)' : 'var(--danger)' }}>
                  {data.anchorage.ok ? '✓ ' : '✗ '}{data.anchorage.note}
                </div>
              </ResultCard>

              <ResultCard title="Flexión a la cara de columna">
                <KV label="X — Mu" value={`${(data.flexion.in_X.Mu / 1e5).toFixed(3)} tonf·m`} />
                <KV label="X — As req." value={`${data.flexion.in_X.As_required.toFixed(2)} cm²`} />
                <KV label="X — As prov." value={`${data.flexion.in_X.As_provided.toFixed(2)} cm²`} />
                <KV label="X — Barras" value={data.flexion.in_X.label} mono />
                <div className="mt-2">
                  <RhoBar
                    title="ρ dirección X — As / (Ly · d_x)"
                    rho={data.flexion.in_X.rho}
                    rho_min={data.flexion.in_X.rho_min}
                    rho_max={0.04}
                    hideAdvice
                  />
                </div>
                <div className="mt-3 mb-1 border-t" style={{ borderColor: 'var(--border-subtle)' }} />
                <KV label="Y — Mu" value={`${(data.flexion.in_Y.Mu / 1e5).toFixed(3)} tonf·m`} />
                <KV label="Y — As req." value={`${data.flexion.in_Y.As_required.toFixed(2)} cm²`} />
                <KV label="Y — As prov." value={`${data.flexion.in_Y.As_provided.toFixed(2)} cm²`} />
                <KV label="Y — Barras" value={data.flexion.in_Y.label} mono />
                <div className="mt-2">
                  <RhoBar
                    title="ρ dirección Y — As / (Lx · d_y)"
                    rho={data.flexion.in_Y.rho}
                    rho_min={data.flexion.in_Y.rho_min}
                    rho_max={0.04}
                    hideAdvice
                  />
                </div>
                <div className="mt-1 text-[10px]" style={{ color: data.flexion.ok ? 'var(--success)' : 'var(--danger)' }}>
                  {data.flexion.ok ? '✓ Flexión OK' : '✗ As provisto < As requerido — aumentar n o Ø'}
                </div>
              </ResultCard>

              <ResultCard title="Verificaciones">
                {data.verifications.map((v) => (
                  <div key={v.id} className="flex items-start gap-2 py-0.5">
                    <span
                      className="mt-0.5"
                      style={{ color: v.ok ? 'var(--success)' : 'var(--danger)', fontWeight: 700, minWidth: 12 }}
                    >
                      {v.ok ? '✓' : '✗'}
                    </span>
                    <span className="flex-1 text-[10px]" style={{ lineHeight: 1.35 }}>
                      {v.label}
                    </span>
                  </div>
                ))}
              </ResultCard>
            </>
          )}
        </aside>
      </div>

      {/* HIDDEN 3D viewer for PDF snapshot capture */}
      {data && (
        <div
          ref={snapshotContainerRef}
          aria-hidden="true"
          style={{
            position: 'fixed',
            top: 0,
            left: -2400,
            width: 1100,
            height: 600,
            pointerEvents: 'none',
            opacity: 0,
          }}
        >
          <Footing3DView
            Lx={request.section.Lx}
            Ly={request.section.Ly}
            Hf={request.section.Hf}
            bc={request.section.bc}
            hc={request.section.hc}
            df={request.section.df}
            cover={request.materials.cover}
            diam_x={request.reinforcement.diam_x}
            n_x={request.reinforcement.n_x}
            diam_y={request.reinforcement.diam_y}
            n_y={request.reinforcement.n_y}
          />
        </div>
      )}
    </div>
  );
}

/* ====================================================================== */
/*  Inputs                                                                  */
/* ====================================================================== */

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-sm)] border p-2" style={{ borderColor: 'var(--border-default)' }}>
      <div className="mb-1.5 text-[9px] font-semibold uppercase tracking-[0.16em]" style={{ color: 'var(--text-muted)' }}>
        {title}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function NumberRow({ label, value, onChange, step = 1 }: { label: string; value: number; onChange: (v: number) => void; step?: number }) {
  return (
    <label className="flex items-center justify-between gap-2 py-0.5">
      <span className="text-[10.5px]" style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <input
        type="number"
        value={value}
        step={step}
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          if (!Number.isNaN(v)) onChange(v);
        }}
        className="w-[120px] rounded-[var(--radius-sm)] border px-2 py-1 text-right text-[11px] font-mono"
        style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
      />
    </label>
  );
}

function SelectRow({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-2 py-0.5">
      <span className="text-[10.5px]" style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-[120px] rounded-[var(--radius-sm)] border px-2 py-1 text-[11px]"
        style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

/* ====================================================================== */
/*  Results cards                                                           */
/* ====================================================================== */

function ResultCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-sm)] border p-2"
      style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)' }}>
      <div className="mb-1.5 text-[9px] font-semibold uppercase tracking-[0.16em]" style={{ color: 'var(--text-muted)' }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function KV({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between py-0.5">
      <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span className={mono ? 'font-mono text-[10.5px]' : 'text-[10.5px]'} style={{ color: 'var(--text-primary)' }}>
        {value}
      </span>
    </div>
  );
}

function DiagramCard({
  title,
  children,
  toolbar,
}: {
  title: string;
  children: React.ReactNode;
  toolbar?: React.ReactNode;
}) {
  return (
    <div className="rounded-[var(--radius-md)] border p-3"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}>
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>
          {title}
        </div>
        {toolbar}
      </div>
      <div className="flex justify-center">{children}</div>
    </div>
  );
}

/* ====================================================================== */
/*  PLAN VIEW                                                               */
/* ====================================================================== */

function FootingPlanDiagram({
  Lx, Ly, bc, hc, x_col, y_col, n_x, n_y, diam_x, diam_y,
  d_x, d_y,
  punch_b0_x, punch_b0_y,
  onColumnMove,
}: {
  Lx: number; Ly: number; bc: number; hc: number;
  x_col: number; y_col: number;
  n_x: number; n_y: number; diam_x: string; diam_y: string;
  d_x: number; d_y: number;
  punch_b0_x: number; punch_b0_y: number;
  onColumnMove?: (x_col: number, y_col: number) => void;
}) {
  const W = 880;
  const PAD = 56;
  const drawW = W - 2 * PAD;
  const maxL = Math.max(Lx, Ly, 1);
  const scale = Math.min(drawW / maxL, 380 / maxL);
  const wpx = Lx * scale;
  const hpx = Ly * scale;
  const xL = (W - wpx) / 2;
  const yT = 36;
  const H = yT + hpx + 80;

  // Column position (cm from lower-left). SVG y axis is INVERTED — so y_col
  // (from the bottom edge) maps to (yT + hpx) - y_col*scale.
  const colCx = xL + x_col * scale;
  const colCy = yT + (Ly - y_col) * scale;

  const colW = bc * scale;
  const colH = hc * scale;
  const colX = colCx - colW / 2;
  const colY = colCy - colH / 2;

  // ---- Drag handling — move the column with the mouse --------------------
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [dragging, setDragging] = useState(false);

  const handlePointerDown = (e: React.PointerEvent<SVGRectElement>) => {
    if (!onColumnMove) return;
    e.preventDefault();
    (e.target as Element).setPointerCapture(e.pointerId);
    setDragging(true);
  };
  const handlePointerMove = (e: React.PointerEvent<SVGRectElement>) => {
    if (!dragging || !onColumnMove) return;
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    // Convert client coords -> SVG viewBox coords
    const svgX = ((e.clientX - rect.left) / rect.width) * W;
    const svgY = ((e.clientY - rect.top) / rect.height) * H;
    // Convert SVG coords -> cm in footing frame
    const x_cm = (svgX - xL) / scale;
    const y_cm = Ly - (svgY - yT) / scale;
    // Clamp so the column stays fully inside the footing
    const xClamped = Math.max(bc / 2, Math.min(Lx - bc / 2, x_cm));
    const yClamped = Math.max(hc / 2, Math.min(Ly - hc / 2, y_cm));
    // Snap to 1 cm so the input shows clean numbers
    onColumnMove(Math.round(xClamped), Math.round(yClamped));
  };
  const handlePointerUp = (e: React.PointerEvent<SVGRectElement>) => {
    if (!dragging) return;
    try { (e.target as Element).releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    setDragging(false);
  };

  // Punching critical perimeter (still centered on the column)
  const punchW = punch_b0_x * scale;
  const punchH = punch_b0_y * scale;
  const punchX = colCx - punchW / 2;
  const punchY = colCy - punchH / 2;

  // One-way critical sections — at d from EACH column face (4 lines, since
  // cantilevers may be asymmetric):
  //   X-direction critical lines at x_col ± (bc/2 + d_x)
  //   Y-direction critical lines at y_col ± (hc/2 + d_y)
  const owX_left = colCx - (bc / 2 + d_x) * scale;
  const owX_right = colCx + (bc / 2 + d_x) * scale;
  const owY_top = colCy - (hc / 2 + d_y) * scale;
  const owY_bot = colCy + (hc / 2 + d_y) * scale;

  return (
    <svg
      ref={svgRef}
      width={W} height={H} viewBox={`0 0 ${W} ${H}`}
      style={{ width: '100%', height: 'auto', maxWidth: W, userSelect: 'none' }}
    >
      <defs>
        <pattern id="hatch-foot" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="var(--text-muted)" strokeOpacity="0.20" strokeWidth="1" />
        </pattern>
      </defs>

      {/* Footing outline */}
      <rect x={xL} y={yT} width={wpx} height={hpx} fill="url(#hatch-foot)" stroke="var(--concrete-outline)" strokeWidth="1.4" />

      {/* Bars in X direction (horizontal lines spread across Y) */}
      {Array.from({ length: n_x }).map((_, i) => {
        const y = yT + ((i + 1) * hpx) / (n_x + 1);
        return <line key={`bx-${i}`} x1={xL + 6} y1={y} x2={xL + wpx - 6} y2={y} stroke="var(--accent)" strokeWidth="1.6" />;
      })}
      {/* Bars in Y direction (vertical lines spread across X) */}
      {Array.from({ length: n_y }).map((_, i) => {
        const x = xL + ((i + 1) * wpx) / (n_y + 1);
        return <line key={`by-${i}`} x1={x} y1={yT + 6} x2={x} y2={yT + hpx - 6} stroke="var(--brand-violet, #8B5CF6)" strokeWidth="1.4" strokeDasharray="3 2" />;
      })}

      {/* Column at the actual position — draggable */}
      <rect
        x={colX} y={colY} width={colW} height={colH}
        fill={dragging ? 'rgba(245,158,11,0.35)' : 'rgba(245,158,11,0.20)'}
        stroke="var(--accent)" strokeWidth={dragging ? 2 : 1.5}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{
          cursor: onColumnMove ? (dragging ? 'grabbing' : 'grab') : 'default',
          touchAction: 'none',
        }}
      />
      <text
        x={colCx} y={colCy + 4} textAnchor="middle" fontSize="11" fontFamily="monospace"
        fill="var(--accent)" style={{ fontWeight: 700, pointerEvents: 'none' }}>
        col {bc}×{hc}
      </text>
      {/* Tiny "drag" indicator (four arrows) shown in the corner of the column */}
      {onColumnMove && (
        <g pointerEvents="none" opacity={dragging ? 1 : 0.55}>
          <text x={colCx} y={colCy - colH / 2 - 4} textAnchor="middle" fontSize="9.5"
            fontFamily="monospace" fill="var(--accent)" style={{ fontWeight: 700 }}>
            ⤧ arrastra para mover
          </text>
        </g>
      )}
      {/* Crosshairs to highlight column center when eccentric */}
      <line x1={colCx} y1={yT} x2={colCx} y2={yT + hpx} stroke="var(--accent)" strokeWidth="0.6" strokeDasharray="2 3" opacity="0.4" pointerEvents="none" />
      <line x1={xL} y1={colCy} x2={xL + wpx} y2={colCy} stroke="var(--accent)" strokeWidth="0.6" strokeDasharray="2 3" opacity="0.4" pointerEvents="none" />

      {/* Punching critical perimeter (dashed teal) — around the column */}
      <rect
        x={punchX} y={punchY} width={punchW} height={punchH}
        fill="none" stroke="#0891B2" strokeWidth="1.4" strokeDasharray="6 4"
      />

      {/* One-way critical sections — at d from each column face */}
      <line x1={owX_left} y1={yT} x2={owX_left} y2={yT + hpx} stroke="#22C55E" strokeWidth="1.2" strokeDasharray="4 3" />
      <line x1={owX_right} y1={yT} x2={owX_right} y2={yT + hpx} stroke="#22C55E" strokeWidth="1.2" strokeDasharray="4 3" />
      <line x1={xL} y1={owY_top} x2={xL + wpx} y2={owY_top} stroke="#22C55E" strokeWidth="1.2" strokeDasharray="4 3" />
      <line x1={xL} y1={owY_bot} x2={xL + wpx} y2={owY_bot} stroke="#22C55E" strokeWidth="1.2" strokeDasharray="4 3" />

      {/* Dimensions */}
      <text x={xL + wpx / 2} y={yT + hpx + 22} textAnchor="middle" fontSize="12" fontFamily="monospace" fill="var(--text-secondary)" style={{ fontWeight: 600 }}>
        Lx = {Lx} cm
      </text>
      <text x={xL - 12} y={yT + hpx / 2} textAnchor="middle" fontSize="12" fontFamily="monospace" fill="var(--text-secondary)" style={{ fontWeight: 600 }}
        transform={`rotate(-90, ${xL - 12}, ${yT + hpx / 2})`}>
        Ly = {Ly} cm
      </text>
      {/* Show x_col, y_col annotation when off-center */}
      {(Math.abs(x_col - Lx / 2) > 0.5 || Math.abs(y_col - Ly / 2) > 0.5) && (
        <text x={colCx + colW / 2 + 6} y={colCy - colH / 2 - 6} fontSize="9.5" fontFamily="monospace" fill="var(--accent)">
          (x={x_col.toFixed(0)}, y={y_col.toFixed(0)}) cm
        </text>
      )}

      {/* Legend */}
      <g transform={`translate(${PAD - 8}, ${yT + hpx + 42})`}>
        <line x1="0" y1="0" x2="22" y2="0" stroke="var(--accent)" strokeWidth="1.8" />
        <text x="28" y="3" fontSize="10" fill="var(--text-secondary)" fontFamily="monospace">
          acero X · {n_x}Ø{diam_x}"
        </text>
        <line x1="190" y1="0" x2="212" y2="0" stroke="var(--brand-violet, #8B5CF6)" strokeWidth="1.6" strokeDasharray="3 2" />
        <text x="218" y="3" fontSize="10" fill="var(--text-secondary)" fontFamily="monospace">
          acero Y · {n_y}Ø{diam_y}"
        </text>
        <line x1="380" y1="0" x2="402" y2="0" stroke="#0891B2" strokeWidth="1.6" strokeDasharray="6 4" />
        <text x="408" y="3" fontSize="10" fill="var(--text-secondary)" fontFamily="monospace">
          punzonado a d/2
        </text>
        <line x1="540" y1="0" x2="562" y2="0" stroke="#22C55E" strokeWidth="1.4" strokeDasharray="4 3" />
        <text x="568" y="3" fontSize="10" fill="var(--text-secondary)" fontFamily="monospace">
          cortante 1D a d
        </text>
      </g>
    </svg>
  );
}

/* ====================================================================== */
/*  ELEVATION DIAGRAM (with bearing-pressure stress)                        */
/* ====================================================================== */

function FootingElevationDiagram({
  Lx, Hf, bc, x_col, df, sigma_max, sigma_min, sigma_adm_eff,
  hooked, hook_tail,
}: {
  Lx: number; Hf: number; bc: number; x_col: number; df: number;
  sigma_max: number; sigma_min: number; sigma_adm_eff: number;
  hooked: boolean; hook_tail: number;
}) {
  const W = 880;
  const PAD = 56;
  const drawW = W - 2 * PAD;
  const totalH = df + 80;
  const scaleX = drawW / Lx;
  const scaleY = Math.min(scaleX, 220 / Math.max(totalH, 1));
  const wpx = Lx * scaleX;
  const xL = (W - wpx) / 2;
  const yGrade = 50;
  const ySlabTop = yGrade + (df - Hf) * scaleY;
  const ySlabBot = ySlabTop + Hf * scaleY;
  const H = ySlabBot + 120;

  // Stress diagram below the slab
  const stress_H_max = 60;
  const sMax = Math.max(sigma_max, sigma_adm_eff);
  const stressH_max = Math.min(stress_H_max, (sigma_max / sMax) * stress_H_max);
  const stressH_min = Math.min(stress_H_max, Math.max(sigma_min, 0) / sMax * stress_H_max);
  const stressY1 = ySlabBot + 18;
  const stressLeftY = stressY1 - stressH_max;
  const stressRightY = stressY1 - stressH_min;

  // Column at the actual x_col position
  const colW = bc * scaleX;
  const colCx = xL + x_col * scaleX;
  const colX = colCx - colW / 2;
  const colTop = yGrade - 50;
  const colBot = ySlabTop;

  // Column dowels (mechones) — drawn schematically as two amber lines
  // descending into the footing with a 90° hook tail at the bottom
  const dowelInset = 4 * scaleX;
  const dowelLeftX = colX + dowelInset;
  const dowelRightX = colX + colW - dowelInset;
  const dowelTopY = colTop + 6;
  const dowelBotY = ySlabBot - 8;     // stop just inside the bottom (above mesh)
  const hookTailPx = hook_tail * scaleX;

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', maxWidth: W }}>
      <defs>
        <pattern id="hatch-elev" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="var(--text-muted)" strokeOpacity="0.20" strokeWidth="1" />
        </pattern>
        <pattern id="soil-pat" patternUnits="userSpaceOnUse" width="8" height="8">
          <rect width="8" height="8" fill="#7C2D12" fillOpacity="0.12" />
          <circle cx="2" cy="2" r="0.7" fill="#7C2D12" fillOpacity="0.4" />
          <circle cx="6" cy="6" r="0.7" fill="#7C2D12" fillOpacity="0.4" />
        </pattern>
      </defs>

      {/* Soil block */}
      <rect x={xL - 40} y={yGrade} width={wpx + 80} height={(df - Hf) * scaleY + Hf * scaleY + 10} fill="url(#soil-pat)" />
      {/* Grade line */}
      <line x1={xL - 60} y1={yGrade} x2={xL + wpx + 60} y2={yGrade} stroke="#7C2D12" strokeWidth="1.5" />
      <text x={xL - 60} y={yGrade - 4} fontSize="10" fontFamily="monospace" fill="#7C2D12">NPT (grade)</text>

      {/* Footing slab */}
      <rect
        x={xL} y={ySlabTop} width={wpx} height={Hf * scaleY}
        fill="url(#hatch-elev)" stroke="var(--concrete-outline)" strokeWidth="1.4"
      />
      {/* Column */}
      <rect
        x={colX} y={colTop} width={colW} height={colBot - colTop}
        fill="rgba(148,163,184,0.25)" stroke="var(--concrete-outline)" strokeWidth="1.3"
      />

      {/* COLUMN DOWELS (mechones) descending into the footing, with optional
          90° hook at the bottom bending OUTWARD. */}
      {[dowelLeftX, dowelRightX].map((dx, i) => {
        const outward = i === 0 ? -1 : +1;
        return (
          <g key={`dow-${i}`}>
            {/* Vertical part */}
            <line x1={dx} y1={dowelTopY} x2={dx} y2={dowelBotY}
              stroke="var(--accent)" strokeWidth="2.2" />
            {/* 90° hook tail */}
            {hooked && (
              <line
                x1={dx}
                y1={dowelBotY}
                x2={dx + outward * hookTailPx}
                y2={dowelBotY}
                stroke="var(--accent)"
                strokeWidth="2.2"
              />
            )}
          </g>
        );
      })}
      {/* Hook annotation */}
      {hooked && (
        <text x={colX + colW / 2} y={dowelBotY + 14} textAnchor="middle"
          fontSize="9" fontFamily="monospace" fill="var(--accent)" style={{ fontStyle: 'italic' }}>
          gancho 90° · cola {hook_tail.toFixed(0)} cm
        </text>
      )}

      {/* Stress diagram trapezoid under the slab */}
      <polygon
        points={`${xL},${stressY1} ${xL + wpx},${stressY1} ${xL + wpx},${stressRightY} ${xL},${stressLeftY}`}
        fill="rgba(220,38,38,0.18)"
        stroke="var(--danger)"
        strokeWidth="1.4"
      />
      {/* σ labels */}
      <text x={xL - 6} y={stressLeftY - 4} textAnchor="end" fontSize="10" fontFamily="monospace" fill="var(--danger)" style={{ fontWeight: 700 }}>
        σ_max = {sigma_max.toFixed(3)} kgf/cm²
      </text>
      <text x={xL + wpx + 6} y={stressRightY - 4} fontSize="10" fontFamily="monospace" fill="var(--danger)" style={{ fontWeight: 700 }}>
        σ_min = {sigma_min.toFixed(3)} kgf/cm²
      </text>

      {/* σ_adm reference line */}
      <line
        x1={xL} y1={stressY1 - stress_H_max}
        x2={xL + wpx} y2={stressY1 - stress_H_max}
        stroke="var(--success)" strokeWidth="1.2" strokeDasharray="4 3"
      />
      <text x={xL + wpx + 6} y={stressY1 - stress_H_max + 3} fontSize="10" fontFamily="monospace" fill="var(--success)">
        σ_adm = {sigma_adm_eff.toFixed(3)}
      </text>

      {/* Dimensions */}
      <text x={(xL + xL + wpx) / 2} y={ySlabBot + 12} textAnchor="middle" fontSize="11" fontFamily="monospace" fill="var(--text-secondary)">
        Lx = {Lx} cm  ·  Hf = {Hf} cm  ·  df = {df} cm
      </text>
    </svg>
  );
}

/* ====================================================================== */
/*  BEARING DESIGN ENVELOPE — (P, M) valid pairs given soil capacity        */
/* ====================================================================== */

/**
 * Plots the bearing-pressure design envelope for the current footing in
 * service. The footing geometry (Lx, Ly, Hf, df) and σ_adm define a
 * diamond-shaped region of (P_col, M) pairs that simultaneously satisfy:
 *
 *   σ_max  = (P_col + W)/A + M/S      ≤  σ_adm
 *   σ_min  = (P_col + W)/A − M/S      ≥  0   (no uplift / full contact)
 *
 * where W is the self-weight + soil-above weight (fixed for the geometry)
 * and S is the section modulus in the bending direction.
 *
 * Two envelopes are drawn — one per bending direction:
 *   Eje X  : moment about the Y-axis (My) ⇒ uses S_x = Ly·Lx²/6
 *   Eje Y  : moment about the X-axis (Mx) ⇒ uses S_y = Lx·Ly²/6
 *
 * The user's demand is plotted as two red dots:
 *   (P, |My_eff|)  on the X envelope
 *   (P, |Mx_eff|)  on the Y envelope
 * Both must lie inside their respective polygons for the bearing check
 * to pass.
 */
function BearingEnvelopeChart({
  Lx, Ly, Hf, df, bc, hc,
  gamma_s, gamma_c, sigma_adm_eff,
  P, Mx, My, x_col, y_col,
}: {
  Lx: number; Ly: number; Hf: number; df: number; bc: number; hc: number;
  gamma_s: number; gamma_c: number; sigma_adm_eff: number;
  P: number; Mx: number; My: number;
  x_col: number; y_col: number;
}) {
  // Dead weights (kgf)
  const A = Lx * Ly;
  const W_self = Lx * Ly * Hf * gamma_c * 1e-6;
  const soil_depth = Math.max(df - Hf, 0);
  const W_soil = (Lx * Ly - bc * hc) * soil_depth * gamma_s * 1e-6;
  const W = W_self + W_soil;

  // Section moduli (cm³)
  const Sx = Ly * Lx * Lx / 6;  // for My moments (variation in X)
  const Sy = Lx * Ly * Ly / 6;  // for Mx moments (variation in Y)

  // Diamond vertices for a given S, in (P_col, M) space (kgf, kgf·cm):
  //   Left tip:    (P_col = -W,                       M = 0)
  //   Top tip:     (P_col = σ_adm·A/2 - W,            M = +σ_adm·S/2)
  //   Right tip:   (P_col = σ_adm·A - W,              M = 0)
  //   Bottom tip:  same x as top, M = -σ_adm·S/2
  const P_left = -W;
  const P_mid = sigma_adm_eff * A / 2 - W;
  const P_right = sigma_adm_eff * A - W;
  const M_peak_x = sigma_adm_eff * Sx / 2;
  const M_peak_y = sigma_adm_eff * Sy / 2;

  // Demand point (effective moments include the column's geometric offset)
  const offX_cm = x_col - Lx / 2;
  const offY_cm = y_col - Ly / 2;
  const My_eff = My + P * offX_cm;
  const Mx_eff = Mx + P * offY_cm;
  // Total axial including self-weight + soil above (for σ checks the
  // bearing formula already includes W via the envelope shift)
  const P_demand = P;

  // Plot range — use the X envelope (larger of the two for non-square)
  const M_peak_max = Math.max(M_peak_x, M_peak_y, Math.abs(My_eff), Math.abs(Mx_eff), 1);
  const Pmin_plot = Math.min(P_left, P_demand) - Math.max(0.05 * Math.abs(P_right - P_left), 1000);
  const Pmax_plot = Math.max(P_right, P_demand) + Math.max(0.05 * Math.abs(P_right - P_left), 1000);
  const Mmax_plot = M_peak_max * 1.15;

  const W_svg = 880;
  const H_svg = 360;
  const padL = 60;
  const padR = 60;
  const padT = 32;
  const padB = 52;
  const drawW = W_svg - padL - padR;
  const drawH = H_svg - padT - padB;

  // Convert kgf -> tonf, kgf·cm -> tonf·m for display
  const xOf = (P_kgf: number) =>
    padL + ((P_kgf - Pmin_plot) / (Pmax_plot - Pmin_plot)) * drawW;
  const yOf = (M_kgf_cm: number) =>
    padT + drawH / 2 - (M_kgf_cm / Mmax_plot) * (drawH / 2);

  // Envelope polygons
  const env_x = [
    [P_left, 0],
    [P_mid, M_peak_x],
    [P_right, 0],
    [P_mid, -M_peak_x],
  ] as const;
  const env_y = [
    [P_left, 0],
    [P_mid, M_peak_y],
    [P_right, 0],
    [P_mid, -M_peak_y],
  ] as const;
  const polyPoints = (verts: readonly (readonly [number, number])[]) =>
    verts.map(([p, m]) => `${xOf(p).toFixed(1)},${yOf(m).toFixed(1)}`).join(' ');

  // Inside-the-envelope check for each demand point
  const inside_X =
    P_demand >= P_left && P_demand <= P_right &&
    Math.abs(My_eff) <= (sigma_adm_eff * Sx / 2) *
      (P_demand <= P_mid ? (P_demand - P_left) / (P_mid - P_left) : (P_right - P_demand) / (P_right - P_mid));
  const inside_Y =
    P_demand >= P_left && P_demand <= P_right &&
    Math.abs(Mx_eff) <= (sigma_adm_eff * Sy / 2) *
      (P_demand <= P_mid ? (P_demand - P_left) / (P_mid - P_left) : (P_right - P_demand) / (P_right - P_mid));

  // X tick positions (in P, every "nice" step)
  const P_range_tonf = (Pmax_plot - Pmin_plot) / 1000;
  const P_step_tonf = niceStep(P_range_tonf / 6);
  const P_ticks: number[] = [];
  for (let p = Math.ceil(Pmin_plot / 1000 / P_step_tonf) * P_step_tonf; p * 1000 <= Pmax_plot; p += P_step_tonf) {
    P_ticks.push(p);
  }
  const M_range_tnm = (Mmax_plot * 2) / 1e5;
  const M_step_tnm = niceStep(M_range_tnm / 6);
  const M_ticks: number[] = [];
  for (let m = -Math.floor(Mmax_plot / 1e5 / M_step_tnm) * M_step_tnm; m * 1e5 <= Mmax_plot; m += M_step_tnm) {
    M_ticks.push(m);
  }

  return (
    <svg width={W_svg} height={H_svg} viewBox={`0 0 ${W_svg} ${H_svg}`} style={{ width: '100%', height: 'auto', maxWidth: W_svg }}>
      {/* X envelope — eje X (moment about Y, S_x) */}
      <polygon
        points={polyPoints(env_x)}
        fill="rgba(245,158,11,0.10)"
        stroke="var(--accent)"
        strokeWidth="1.4"
        strokeDasharray="6 0"
      />
      {/* Y envelope — eje Y (moment about X, S_y) */}
      <polygon
        points={polyPoints(env_y)}
        fill="rgba(139,92,246,0.08)"
        stroke="var(--brand-violet, #8B5CF6)"
        strokeWidth="1.4"
        strokeDasharray="4 2"
      />

      {/* Axes */}
      <line x1={padL} y1={yOf(0)} x2={W_svg - padR} y2={yOf(0)} stroke="var(--text-muted)" strokeWidth="0.8" />
      <line x1={xOf(0)} y1={padT} x2={xOf(0)} y2={H_svg - padB} stroke="var(--text-muted)" strokeWidth="0.8" />

      {/* Ticks */}
      {P_ticks.map((t) => (
        <g key={`pt-${t}`}>
          <line x1={xOf(t * 1000)} y1={yOf(0) - 3} x2={xOf(t * 1000)} y2={yOf(0) + 3} stroke="var(--text-muted)" />
          <text x={xOf(t * 1000)} y={H_svg - padB + 14} textAnchor="middle" fontSize="9.5" fontFamily="monospace" fill="var(--text-muted)">
            {t.toFixed(M_step_tnm < 1 ? 1 : 0)}
          </text>
        </g>
      ))}
      {M_ticks.filter((m) => Math.abs(m * 1e5) <= Mmax_plot).map((t) => (
        <g key={`mt-${t}`}>
          <line x1={xOf(0) - 3} y1={yOf(t * 1e5)} x2={xOf(0) + 3} y2={yOf(t * 1e5)} stroke="var(--text-muted)" />
          <text x={xOf(0) - 6} y={yOf(t * 1e5) + 3} textAnchor="end" fontSize="9.5" fontFamily="monospace" fill="var(--text-muted)">
            {t.toFixed(M_step_tnm < 1 ? 1 : 0)}
          </text>
        </g>
      ))}

      {/* Axis labels */}
      <text x={W_svg - padR + 4} y={yOf(0) - 6} fontSize="11" fontFamily="monospace" fill="var(--text-secondary)" style={{ fontWeight: 700 }}>
        P (tonf)
      </text>
      <text x={xOf(0) + 8} y={padT - 6} fontSize="11" fontFamily="monospace" fill="var(--text-secondary)" style={{ fontWeight: 700 }}>
        M (tonf·m)
      </text>

      {/* Vertices labels */}
      <text x={xOf(P_left) - 4} y={yOf(0) - 6} textAnchor="end" fontSize="9" fontFamily="monospace" fill="var(--text-muted)">
        P=−W ({(P_left / 1000).toFixed(1)})
      </text>
      <text x={xOf(P_right) + 4} y={yOf(0) - 6} fontSize="9" fontFamily="monospace" fill="var(--text-muted)">
        σ_adm·A−W ({(P_right / 1000).toFixed(1)})
      </text>

      {/* DEMAND POINTS */}
      {/* (P, |My_eff|) — X-direction (orange) */}
      <line
        x1={xOf(P_demand)} y1={yOf(0)}
        x2={xOf(P_demand)} y2={yOf(My_eff)}
        stroke="var(--accent)" strokeWidth="1" strokeDasharray="3 2" opacity="0.5"
      />
      <circle
        cx={xOf(P_demand)} cy={yOf(My_eff)}
        r={6}
        fill={inside_X ? 'var(--success)' : 'var(--danger)'}
        stroke="white" strokeWidth="1.5"
      />
      <text x={xOf(P_demand) + 10} y={yOf(My_eff) - 6}
        fontSize="10" fontFamily="monospace" fill={inside_X ? 'var(--success)' : 'var(--danger)'} style={{ fontWeight: 700 }}>
        (P, My_eff) {inside_X ? '✓' : '✗'}
      </text>

      {/* (P, |Mx_eff|) — Y-direction (violet) */}
      <circle
        cx={xOf(P_demand)} cy={yOf(Mx_eff)}
        r={5}
        fill={inside_Y ? 'var(--success)' : 'var(--danger)'}
        stroke="white" strokeWidth="1.5"
      />
      <text x={xOf(P_demand) + 10} y={yOf(Mx_eff) + 14}
        fontSize="10" fontFamily="monospace" fill={inside_Y ? 'var(--success)' : 'var(--danger)'} style={{ fontWeight: 700 }}>
        (P, Mx_eff) {inside_Y ? '✓' : '✗'}
      </text>

      {/* Legend */}
      <g transform={`translate(${padL}, ${H_svg - 16})`}>
        <line x1="0" y1="0" x2="18" y2="0" stroke="var(--accent)" strokeWidth="1.4" />
        <text x="22" y="3" fontSize="10" fontFamily="monospace" fill="var(--accent)">
          envolvente eje X (My, S_x = Ly·Lx²/6)
        </text>
        <line x1="280" y1="0" x2="298" y2="0" stroke="var(--brand-violet, #8B5CF6)" strokeWidth="1.4" strokeDasharray="4 2" />
        <text x="302" y="3" fontSize="10" fontFamily="monospace" fill="var(--brand-violet, #8B5CF6)">
          envolvente eje Y (Mx, S_y = Lx·Ly²/6)
        </text>
        <text x="540" y="3" fontSize="9.5" fontFamily="monospace" fill="var(--text-muted)" style={{ fontStyle: 'italic' }}>
          W_propio + suelo = {(W / 1000).toFixed(2)} tonf
        </text>
      </g>
    </svg>
  );
}

function niceStep(rawRange: number): number {
  if (!isFinite(rawRange) || rawRange <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(rawRange)));
  const candidates = [1, 2, 2.5, 5, 10];
  return pow * (candidates.find((c) => c * pow >= rawRange) ?? 10);
}
